import React, { useEffect, useState, useMemo } from "react";
import {
  DesktopPopupModal,
  IconName,
  StarRatingsSelection,
  HotelPriceSelection,
  FreeCancelFilter,
  ActionLink,
  ActionButton,
  MobileFloatingButton,
  MobilePopoverCard,
  CloseButtonIcon,
  HotelAvailabilitySort,
  IHotelAvailabilitySortBaseProps,
  Icon,
  PremiumStaysFilter,
  IPremiumStaysFilterBase,
  ILifestyleStaysFilterBase,
  LifestyleStaysFilter,
  HotelsOnSaleFilter,
} from "halifax";
import clsx from "clsx";
import { Box, Divider, Tooltip, Typography } from "@material-ui/core";
import {
  HotelStarRatingEnum,
  IPriceRange,
  HomeRoomsAndBeds,
  VacationRentalAmenityKindEnum as VRAmenity,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import isEqual from "lodash-es/isEqual";
import { VacationRentalRoomsAndBedsSelection } from "./components/VacationRentalRoomsAndBedsSelection";
import { VacationRentalAmenitiesSelection } from "./components/VacationRentalAmenitiesSelection";
import styles from "./styles.module.scss";
import { LIFESTYLE_STAYS_LABEL, PREMIUM_STAYS_LABEL } from "./textConstants";

export interface VacationRentalAvailabilityFilterProps
  extends IHotelAvailabilitySortBaseProps,
    IPremiumStaysFilterBase,
    ILifestyleStaysFilterBase {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  readyToReset: boolean;
  // Star ratings filter
  starRatings: HotelStarRatingEnum[];
  setStarRatings: (starRatings: HotelStarRatingEnum[]) => void;
  // Amenities filter
  vrAmenities: VRAmenity[];
  setVrAmenitiesFilter: (amenities: VRAmenity[]) => void;
  // Rooms and Beds filter
  vrRoomsAndBeds?: HomeRoomsAndBeds;
  setVrRoomsAndBedsFilter: (roomsAndBeds?: HomeRoomsAndBeds) => void;
  // Price filter
  currencySymbol: string;
  maxPrice: number;
  minMaxPriceRange: IPriceRange | null;
  setMaxPriceFilter: (value: number) => void;
  // Free cancellation filter
  hasFreeCancelFilter: boolean;
  setFreeCancelFilter: (freeCancel: boolean) => void;
  hasHotelsOnSaleFilter: boolean;
  setHotelsOnSaleFilter: (saleOnly: boolean) => void;
  // Sort
  defaultSortOption: string;
}

export const VacationRentalAvailabilityFilter = ({
  open,
  onClose,
  isMobile,
  readyToReset,
  starRatings,
  vrAmenities,
  setVrAmenitiesFilter,
  setStarRatings,
  vrRoomsAndBeds,
  setVrRoomsAndBedsFilter,
  currencySymbol,
  maxPrice,
  minMaxPriceRange,
  setMaxPriceFilter,
  hasFreeCancelFilter,
  setFreeCancelFilter,
  sortOptions,
  selectedSortOption,
  setSelectedSortOption,
  defaultSortOption,
  premiumStays,
  lifestyleStays,
  hasHotelsOnSaleFilter,
  setHotelsOnSaleFilter,
}: VacationRentalAvailabilityFilterProps) => {
  const [curVrAmenities, setCurVrAmenities] =
    useState<VRAmenity[]>(vrAmenities);

  const [currRoomsAndBeds, setCurrRoomsAndBeds] = useState<
    HomeRoomsAndBeds | undefined
  >(vrRoomsAndBeds);

  const [curStarRatings, setCurStarRatings] =
    useState<HotelStarRatingEnum[]>(starRatings);

  const [curMaxPrice, setCurMaxPrice] = useState<number>(maxPrice);

  const [curHasFreeCancelFilter, setCurHasFreeCancelFilter] =
    useState<boolean>(hasFreeCancelFilter);

  const [curSortOption, setCurSortOption] =
    useState<string>(selectedSortOption);
  const [curPremiumStays, setCurPremiumStays] = useState<boolean>(
    premiumStays?.value ?? false
  );
  const [curLifestyleStays, setCurLifestyleStays] = useState<boolean>(
    lifestyleStays?.value ?? false
  );

  const [curHasHotelsOnSaleFilter, setCurHasHotelsOnSaleFilter] =
    React.useState<boolean>(hasHotelsOnSaleFilter);

  const memoizedPremiumStays = useMemo(() => {
    if (!premiumStays) return undefined;
    return {
      ...premiumStays,
      set: setCurPremiumStays,
    };
  }, [premiumStays, setCurPremiumStays]);

  const memoizedLifestyleStays = useMemo(() => {
    if (!lifestyleStays) return undefined;
    return {
      ...lifestyleStays,
      set: setCurLifestyleStays,
    };
  }, [lifestyleStays, setCurLifestyleStays]);

  useEffect(() => {
    setCurVrAmenities(vrAmenities);
  }, [vrAmenities, open]);

  useEffect(() => {
    setCurrRoomsAndBeds(vrRoomsAndBeds);
  }, [vrRoomsAndBeds, open]);

  useEffect(() => {
    setCurStarRatings(starRatings);
  }, [starRatings, open]);

  useEffect(() => {
    setCurMaxPrice(maxPrice);
  }, [maxPrice, open]);

  useEffect(() => {
    setCurHasFreeCancelFilter(hasFreeCancelFilter);
  }, [hasFreeCancelFilter, open]);

  useEffect(() => {
    setCurSortOption(selectedSortOption);
  }, [selectedSortOption, open]);

  useEffect(() => {
    setCurPremiumStays(premiumStays?.value ?? false);
  }, [premiumStays, open]);

  useEffect(() => {
    setCurLifestyleStays(lifestyleStays?.value ?? false);
  }, [lifestyleStays, open]);

  React.useEffect(() => {
    setCurHasHotelsOnSaleFilter(hasHotelsOnSaleFilter);
  }, [hasHotelsOnSaleFilter, open]);

  const handleApply = () => {
    setVrAmenitiesFilter(curVrAmenities);
    if (!isEqual(starRatings, curStarRatings)) {
      setStarRatings(curStarRatings);
    }
    setMaxPriceFilter(curMaxPrice);
    setFreeCancelFilter(curHasFreeCancelFilter);
    setVrRoomsAndBedsFilter(currRoomsAndBeds);
    if (isMobile) {
      setSelectedSortOption(curSortOption);
    }
    if (premiumStays && !isEqual(premiumStays.value, curPremiumStays)) {
      premiumStays.set(curPremiumStays);
    }
    if (lifestyleStays && !isEqual(lifestyleStays.value, curLifestyleStays)) {
      lifestyleStays.set(curLifestyleStays);
    }
    setHotelsOnSaleFilter(curHasHotelsOnSaleFilter);
  };

  const canReset = useMemo(
    () =>
      (isMobile && curSortOption !== defaultSortOption) ||
      !isEqual(curVrAmenities, []) ||
      !isEqual(curStarRatings, []) ||
      !isEqual(currRoomsAndBeds, undefined) ||
      curMaxPrice !== 0 ||
      curHasFreeCancelFilter === true ||
      !isEqual(curPremiumStays, premiumStays?.value ?? false) ||
      !isEqual(curLifestyleStays, lifestyleStays?.value ?? false) ||
      curHasHotelsOnSaleFilter === true,
    [
      curVrAmenities,
      curStarRatings,
      currRoomsAndBeds,
      curMaxPrice,
      curHasFreeCancelFilter,
      curSortOption,
      curPremiumStays,
      curLifestyleStays,
      defaultSortOption,
      isMobile,

      curHasFreeCancelFilter,
    ]
  );

  const handleReset = () => {
    setCurVrAmenities([]);
    setCurStarRatings([]);
    setCurMaxPrice(0);
    setCurHasFreeCancelFilter(false);
    setCurrRoomsAndBeds(undefined);
    if (isMobile) {
      setCurSortOption(defaultSortOption);
    }
    setCurPremiumStays(false);
    setCurLifestyleStays(false);
  };

  const optionsChanged = useMemo(
    () =>
      !isEqual(vrAmenities, curVrAmenities) ||
      !isEqual(starRatings, curStarRatings) ||
      maxPrice !== curMaxPrice ||
      hasFreeCancelFilter !== curHasFreeCancelFilter ||
      !isEqual(vrRoomsAndBeds, currRoomsAndBeds) ||
      selectedSortOption !== curSortOption ||
      !isEqual(curPremiumStays, premiumStays?.value ?? false) ||
      !isEqual(curLifestyleStays, lifestyleStays?.value ?? false) ||
      hasHotelsOnSaleFilter !== curHasHotelsOnSaleFilter,
    [
      vrAmenities,
      curVrAmenities,
      starRatings,
      curStarRatings,
      maxPrice,
      curMaxPrice,
      hasFreeCancelFilter,
      curHasFreeCancelFilter,
      vrRoomsAndBeds,
      currRoomsAndBeds,
      selectedSortOption,
      curSortOption,
      premiumStays,
      lifestyleStays,
      curPremiumStays,
      curLifestyleStays,
      hasHotelsOnSaleFilter,
      curHasHotelsOnSaleFilter,
    ]
  );

  const renderFilterButtons = () => (
    <Box
      className={clsx("hotel-availability-filter-bottom-buttons-container", {
        mobile: isMobile,
      })}
    >
      {!isMobile && (
        <>
          <ActionLink
            className={clsx("reset-filters-link", "filter-button", {
              inactive: !readyToReset && !optionsChanged,
            })}
            onClick={handleReset}
            content="Clear all"
          />
          <ActionButton
            className={clsx("apply-filters-button", "filter-button")}
            onClick={() => {
              handleApply();
              onClose();
            }}
            defaultStyle="h4r-primary"
            disabled={!readyToReset && !optionsChanged}
            message="Apply filters"
          />
        </>
      )}
      {isMobile && (
        <MobileFloatingButton
          className="apply-filters-floating-button"
          wrapperClassName="apply-filters-floating-button-container"
          disabled={!optionsChanged}
          onClick={() => {
            handleApply();
            onClose();
          }}
        >
          Apply Filters
        </MobileFloatingButton>
      )}
    </Box>
  );

  const filterContent = (
    <>
      {memoizedPremiumStays && (
        <>
          <PremiumStaysFilter
            title={PREMIUM_STAYS_LABEL}
            isMobile={isMobile}
            icon={IconName.StarIconWithLines}
            premiumStays={memoizedPremiumStays}
          />

          <Divider style={{ border: "none" }} />
        </>
      )}
      {memoizedLifestyleStays && !memoizedPremiumStays && (
        <>
          <LifestyleStaysFilter
            title={LIFESTYLE_STAYS_LABEL}
            isMobile={isMobile}
            icon={IconName.StarIconWithLines}
            lifestyleStays={memoizedLifestyleStays}
          />
          <Divider style={{ border: "none" }} />
        </>
      )}
      <StarRatingsSelection
        title={
          <>
            <span>Vacation rental rating</span>
            <Tooltip
              title={
                <Typography
                  variant="subtitle2"
                  className={styles["filter-tooltip"]}
                >
                  A star rating system is a widely recognized method used to
                  classify and categorize stays based on their overall{" "}
                  <strong>quality, amenities, services,</strong> and{" "}
                  <strong>facilities.</strong> It provides a standardized way
                  for travellers to gauge the level of comfort and luxury they
                  can expect from a particular stay.
                </Typography>
              }
              classes={{
                tooltip: styles["filter-tooltip-text"],
              }}
              placement="bottom"
              tabIndex={0}
              disableFocusListener
            >
              <div className={styles["filter-tooltip-icon"]}>
                <Icon
                  style={{ width: "12px", height: "12px" }}
                  name={IconName.InfoCircle}
                />
              </div>
            </Tooltip>
          </>
        }
        starRatings={curStarRatings}
        setStarRatings={setCurStarRatings}
        icon={IconName.StarOutline}
        isMobile={isMobile}
      />
      <Divider style={{ border: "none" }} />
      <VacationRentalAmenitiesSelection
        isMobile={isMobile}
        variant="filter-modal"
        vrAmenities={curVrAmenities}
        setVrAmenities={setCurVrAmenities}
      />
      <Divider style={{ border: "none" }} />
      <VacationRentalRoomsAndBedsSelection
        isMobile={isMobile}
        vrRoomsAndBeds={currRoomsAndBeds}
        setVrRoomsAndBeds={setCurrRoomsAndBeds}
        variant="filter-modal"
      />
      <Divider style={{ border: "none" }} />
      <HotelPriceSelection
        className="desktop-selection-content"
        title="Max price"
        subtitle="Per night"
        currencySymbol={currencySymbol}
        type="max"
        alwaysShowTooltip
        hotelPriceRange={minMaxPriceRange || { min: 0, max: 0 }}
        reset={() => setCurMaxPrice(0)}
        hotelMaxPrice={curMaxPrice}
        setHotelMaxPrice={setCurMaxPrice}
        showMinLabel
        showClearButton={curMaxPrice !== 0}
        icon={IconName.MoneyOutlineThin}
      />
      <Divider style={{ border: "none" }} />
      <FreeCancelFilter
        icon={IconName.NotAllowedSign}
        className="desktop-selection-content"
        title="Free cancellation"
        hasFreeCancelFilter={curHasFreeCancelFilter}
        setFreeCancelFilter={setCurHasFreeCancelFilter}
        stayTypes={[StayType.Homes]}
      />
      <Divider style={{ border: "none" }} />
      <HotelsOnSaleFilter
        title="Limited-time promotions"
        icon={IconName.PreferredHotelFilter}
        setHotelsOnSaleFilter={setCurHasHotelsOnSaleFilter}
        hasHotelsOnSaleFilter={curHasHotelsOnSaleFilter}
      />
    </>
  );

  return (
    <>
      {isMobile && (
        <MobilePopoverCard
          open={open}
          onClose={onClose}
          fullScreen
          className={clsx("mobile-hotel-availability-filter-root")}
          contentClassName="mobile-hotel-availability-filter-content-wrapper"
          bottomButton={renderFilterButtons()}
          topRightButton={
            <ActionLink
              className="filter-close-button"
              content={<CloseButtonIcon />}
              label="Close"
              onClick={onClose}
            />
          }
          topLeftButton={
            readyToReset || canReset ? (
              <ActionLink content="Clear All Filters" onClick={handleReset} />
            ) : undefined
          }
        >
          <Box className="mobile-hotel-availability-filter-content-container">
            <>
              <HotelAvailabilitySort
                sortOptions={sortOptions}
                selectedSortOption={curSortOption}
                setSelectedSortOption={setCurSortOption}
                title="Sort by"
                icon={IconName.Sort}
              />
              <Divider style={{ border: "none" }} />
              {filterContent}
            </>
          </Box>
        </MobilePopoverCard>
      )}
      {!isMobile && (
        <DesktopPopupModal
          open={open}
          className={clsx("desktop-hotel-availability-filter-root")}
          contentClassName="desktop-hotel-availability-filter-content-wrapper"
          onClose={onClose}
          bottomButton={renderFilterButtons()}
          invisibleBackdrop={false}
        >
          <Box className="desktop-hotel-availability-filter-content-root">
            <Box className="desktop-hotel-availability-filter-content-container">
              <>
                <Typography variant="h2" className="filters-title">
                  Vacation rental filters
                </Typography>
                <Divider style={{ border: "none" }} />
                {filterContent}
              </>
            </Box>
          </Box>
        </DesktopPopupModal>
      )}
    </>
  );
};
